'use client'

import { MorphIcon } from 'morphicons/react'
import { useCallback, useRef, useState } from 'react'

import { ChevronDown, Minus, type IconNode } from '@/lib/icons/lucide-icon-data'
import { usePrefersReducedMotion } from '@/lib/use-prefers-reduced-motion'
import { cn } from '@/lib/utils'

type MorphStateIconProps = {
  /** Lucide icon *data* for the current state; changing it springs to the new shape. */
  icon: IconNode
  size?: number
  className?: string
  /** Override motion preference detection (for tests/stories). */
  prefersReducedMotion?: boolean
}

/**
 * Icon whose shape is driven by state instead of a CSS transform.
 *
 * Use this rather than `rotate-180`-style transitions: the path itself travels to
 * the next icon. Note that congruent pairs (chevron-down/chevron-up) resolve to a
 * pure rotation, because Morphicons refuses to fake a deformation between shapes
 * that only differ by angle — pick shapes that actually differ to see a morph.
 */
export function MorphStateIcon({
  icon,
  size = 16,
  className,
  prefersReducedMotion: prefersReducedMotionProp,
}: MorphStateIconProps) {
  const prefersReducedMotionFromHook = usePrefersReducedMotion()
  const prefersReducedMotion = prefersReducedMotionProp ?? prefersReducedMotionFromHook

  return (
    <MorphIcon
      icon={icon}
      spring="snappy"
      size={size}
      reducedMotion={prefersReducedMotion ? 'user' : 'never'}
      aria-hidden
      className={cn('shrink-0', className)}
    />
  )
}

type MorphExpandIconProps = Omit<MorphStateIconProps, 'icon'> & { expanded: boolean }

/**
 * Expand/collapse affordance: chevron-down collapsed, minus expanded.
 *
 * Deliberately not chevron-down/chevron-up — those are the same shape 180° apart, so
 * the morph degrades to a rotation. Flattening the chevron into a dash is a genuine
 * shape change, and the dash doubles as a "collapse me" affordance.
 */
export function MorphExpandIcon({ expanded, ...props }: MorphExpandIconProps) {
  return <MorphStateIcon icon={expanded ? Minus : ChevronDown} {...props} />
}

type MorphExpandIconFromTriggerProps = Omit<
  MorphExpandIconProps,
  'expanded' | 'prefersReducedMotion'
> & {
  /** Matches the `data-slot` of the Radix trigger this icon lives in. */
  triggerSlot: 'accordion-trigger' | 'collapsible-trigger' | 'select-trigger'
}

/**
 * Expand chevron for Radix triggers that keep open state in context rather than props.
 *
 * Mirrors the enclosing trigger's `data-state`, so call sites inside `AccordionTrigger`,
 * `CollapsibleTrigger` or `SelectTrigger` need no state wiring of their own. Prefer
 * `MorphExpandIcon` wherever the open state is already available as a value.
 */
export function MorphExpandIconFromTrigger({
  triggerSlot,
  ...props
}: MorphExpandIconFromTriggerProps) {
  const observerRef = useRef<MutationObserver | null>(null)
  const [expanded, setExpanded] = useState(false)

  const hostRef = useCallback(
    (node: HTMLSpanElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      if (!node) return

      const trigger = node.closest(`[data-slot="${triggerSlot}"]`)
      if (!trigger) return

      const sync = () => setExpanded(trigger.getAttribute('data-state') === 'open')
      sync()

      const observer = new MutationObserver(sync)
      observer.observe(trigger, { attributes: true, attributeFilter: ['data-state'] })
      observerRef.current = observer
    },
    [triggerSlot],
  )

  return (
    <span ref={hostRef} className="inline-flex">
      <MorphExpandIcon expanded={expanded} {...props} />
    </span>
  )
}
